import { CandidateAssessmentDetail } from "@components/campaigns/CandidateAssessmentDetail";
import {
  RECOMMENDATION_COLORS,
  RECOMMENDATION_LABELS,
} from "@components/campaigns/CandidateAssessmentSummary";
import { useReturnToPool } from "@components/campaigns/useReturnToPool";
import { AddCandidateToListDialog } from "@components/candidates/AddCandidateToListDialog";
import { QueryErrorState } from "@components/shared/QueryErrorState";
import { SlideOverPanel } from "@components/shared/SlideOverPanel";
import { MOVE_CANDIDATE_STAGE } from "@lib/graphql/mutations";
import { ASSESSMENT_STAGE_QUERY, CAMPAIGN_CANDIDATES_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import {
  useCampaignSelection,
  useCandidateSelectionStore,
} from "@lib/stores/candidateSelectionStore";
import { Copy, Plus, Send, Users } from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery } from "urql";
import { AssessmentExportButton, AssessmentSummaryPanel } from "./AssessmentSummaryPanel";
import { CandidateRow } from "./CandidateRow";
import { CopyToCampaignDialog } from "./CopyToCampaignDialog";
import { FindCandidatesPanel } from "./FindCandidatesPanel";
import { StageDropdown, TerminalStageMoveControl } from "./StageDropdown";

interface LonglistStageProps {
  campaignId: string;
}

interface CompetencyScoreData {
  id: string;
  score: number;
  evidence: string | null;
  notes: string | null;
  detailedNotes: string | null;
  capabilityDimension: {
    id: string;
    continuityId: string;
    name: string;
    description: string | null;
    idealScore: number | null;
  };
}

interface AssessmentVersionData {
  overallScore: number | null;
  recommendation: string | null;
  overallSummary: string | null;
  strengths: string | null;
  concerns: string | null;
  culturalFitNotes: string | null;
  trajectoryNotes: string | null;
  riskNotes: string | null;
  versionNumber: number;
  effectiveFrom: string;
  assessor: { id: string; name: string };
  competencyScores: CompetencyScoreData[];
}

interface CampaignCandidateData {
  id: string;
  stage: string;
  source: string | null;
  notes: string | null;
  createdAt: string;
  candidate: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string | null;
    currentRoleTitle: string | null;
    currentCompany: string | null;
    location: string | null;
    linkedinUrl: string | null;
    resumes: { id: string; filename: string; url: string; byteSize: number; createdAt: string }[];
    talentPoolMemberships: { id: string; talentPool: { id: string; name: string } }[];
  };
  addedBy: { id: string; name: string };
  candidateAssessment: {
    id: string;
    currentVersion: AssessmentVersionData | null;
  } | null;
}

interface DimensionData {
  id: string;
  continuityId: string;
  name: string;
  idealScore: number | null;
}

export function LonglistStage({ campaignId }: LonglistStageProps) {
  const [adding, setAdding] = useState(false);
  const [copyingCandidateId, setCopyingCandidateId] = useState<string | null>(null);
  const [viewingAssessmentId, setViewingAssessmentId] = useState<string | null>(null);
  const navigate = useNavigate();

  const selectedIds = useCampaignSelection(campaignId);
  const toggleSelection = useCandidateSelectionStore((s) => s.toggle);
  const setManySelected = useCandidateSelectionStore((s) => s.setMany);
  const clearSelection = useCandidateSelectionStore((s) => s.clear);
  const selectedSet = new Set(selectedIds);

  const [{ data, fetching, error }, reexecute] = useQuery({
    query: CAMPAIGN_CANDIDATES_QUERY,
    variables: { id: campaignId, stage: "longlist" },
  });
  const [{ data: blueprintData }] = useQuery({
    query: ASSESSMENT_STAGE_QUERY,
    variables: { id: campaignId },
  });
  const [, moveStage] = useMutation(MOVE_CANDIDATE_STAGE);

  const allCandidates: CampaignCandidateData[] = data?.campaign?.campaignCandidates ?? [];
  const activeCandidates = allCandidates.filter((cc) => cc.stage === "longlist");
  const movedOnCandidates = allCandidates.filter((cc) => cc.stage !== "longlist");
  const dimensions: DimensionData[] =
    blueprintData?.campaign?.roleBlueprint?.capabilityDimensions ?? [];

  const viewingCandidate = allCandidates.find((cc) => cc.id === viewingAssessmentId);
  const viewingAssessment = viewingCandidate?.candidateAssessment?.currentVersion;

  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const { openFor: openReturnToPool, dialog: returnToPoolDialog } = useReturnToPool(refresh);

  const handleMove = async (campaignCandidateId: string, stage: string) => {
    await moveStage({ input: { campaignCandidateId, stage } });
    refresh();
  };

  function assessmentBadge(cc: CampaignCandidateData): React.ReactNode {
    const assessment = cc.candidateAssessment?.currentVersion;
    if (!assessment) return undefined;

    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setViewingAssessmentId(cc.id);
        }}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-accent/5"
        title="View assessment"
      >
        {assessment.overallScore != null && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
            {assessment.overallScore}/5
          </span>
        )}
        {assessment.recommendation && (
          <span
            className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${RECOMMENDATION_COLORS[assessment.recommendation] ?? ""}`}
          >
            {RECOMMENDATION_LABELS[assessment.recommendation] ?? assessment.recommendation}
          </span>
        )}
      </button>
    );
  }

  function assessmentContent(cc: CampaignCandidateData): React.ReactNode {
    const assessment = cc.candidateAssessment?.currentVersion;
    if (!assessment) return undefined;
    return (
      <CandidateAssessmentDetail
        assessment={assessment}
        candidateName={cc.candidate.fullName}
        campaignCandidateId={cc.id}
        dimensions={dimensions}
        onViewDetails={() => setViewingAssessmentId(cc.id)}
      />
    );
  }

  function poolBadges(cc: CampaignCandidateData) {
    const memberships = cc.candidate.talentPoolMemberships;
    if (!memberships || memberships.length === 0) return undefined;
    return (
      <div className="mt-0.5 flex flex-wrap gap-1">
        {memberships.map((m) => (
          <Link
            key={m.id}
            to={`/talent-pools/${m.talentPool.id}`}
            className="inline-flex items-center rounded-full bg-text-muted/10 px-2 py-0.5 text-[10px] font-medium text-text-muted hover:bg-text-muted/20"
          >
            {m.talentPool.name}
          </Link>
        ))}
      </div>
    );
  }

  if (fetching && allCandidates.length === 0) {
    return <p className="text-sm text-text-muted">Loading longlist...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-text-faint" />
            <h3 className="text-sm font-medium text-text-secondary">Longlist</h3>
            <span className="text-xs text-text-faint">
              {activeCandidates.length} active
              {movedOnCandidates.length > 0 && `, ${movedOnCandidates.length} moved on`}
            </span>
          </div>
          {!adding && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
            >
              <Plus size={12} /> Add Candidate
            </button>
          )}
        </div>

        {adding && (
          <div className="mb-4">
            <AddCandidateToListDialog
              target={{ kind: "campaign", campaignId, stage: "longlist" }}
              heading="Add Candidate to Longlist"
              onDone={() => {
                setAdding(false);
                refresh();
              }}
              onCancel={() => setAdding(false)}
            />
          </div>
        )}

        {error ? (
          <QueryErrorState
            error={error}
            what="the longlist"
            onRetry={refresh}
            retrying={fetching}
          />
        ) : allCandidates.length === 0 && !adding ? (
          <p className="py-6 text-center text-sm text-text-faint">
            No candidates on the longlist yet. Add candidates to get started.
          </p>
        ) : (
          <div>
            {activeCandidates.length > 0 && (
              <SelectionControls
                campaignId={campaignId}
                activeIds={activeCandidates.map((cc) => cc.id)}
                selectedIds={selectedIds}
                onToggleAll={(selectAll) =>
                  setManySelected(
                    campaignId,
                    activeCandidates.map((cc) => cc.id),
                    selectAll,
                  )
                }
                onClear={() => clearSelection(campaignId)}
                onStartOutreach={() => navigate(`/campaigns/${campaignId}/outreach`)}
              />
            )}
            {activeCandidates.map((cc) => (
              <div key={cc.id}>
                <CandidateRow
                  candidate={cc.candidate}
                  source={cc.source}
                  addedBy={cc.addedBy}
                  createdAt={cc.createdAt}
                  badges={poolBadges(cc)}
                  assessmentBadge={assessmentBadge(cc)}
                  assessmentContent={assessmentContent(cc)}
                  selected={selectedSet.has(cc.id)}
                  onToggleSelect={() => toggleSelection(campaignId, cc.id)}
                  campaignId={campaignId}
                  campaignCandidateId={cc.id}
                  actions={
                    <div className="flex gap-1">
                      <StageDropdown
                        currentStage="longlist"
                        onMove={(stage) => handleMove(cc.id, stage)}
                        onReturnToPool={() => openReturnToPool(cc, "longlist")}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setCopyingCandidateId(
                            copyingCandidateId === cc.candidate.id ? null : cc.candidate.id,
                          )
                        }
                        title="Copy to another campaign"
                        className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  }
                />
                {copyingCandidateId === cc.candidate.id && (
                  <div className="mx-3 mb-3">
                    <CopyToCampaignDialog
                      sourceCampaignId={campaignId}
                      candidateId={cc.candidate.id}
                      candidateName={cc.candidate.fullName}
                      onDone={() => setCopyingCandidateId(null)}
                      onCancel={() => setCopyingCandidateId(null)}
                    />
                  </div>
                )}
              </div>
            ))}
            {movedOnCandidates.map((cc) => (
              <CandidateRow
                key={cc.id}
                candidate={cc.candidate}
                source={cc.source}
                addedBy={cc.addedBy}
                createdAt={cc.createdAt}
                badges={poolBadges(cc)}
                assessmentBadge={assessmentBadge(cc)}
                assessmentContent={assessmentContent(cc)}
                currentStage={cc.stage}
                movedPast
                campaignId={campaignId}
                campaignCandidateId={cc.id}
                actions={
                  <TerminalStageMoveControl
                    stage={cc.stage}
                    onMove={(stage) => handleMove(cc.id, stage)}
                  />
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* Find matching candidates */}
      <FindCandidatesPanel campaignId={campaignId} onAdded={refresh} />

      <SlideOverPanel
        open={viewingAssessmentId != null}
        onClose={() => setViewingAssessmentId(null)}
        title={viewingCandidate?.candidate.fullName ?? ""}
      >
        {viewingCandidate && viewingAssessment ? (
          <AssessmentSummaryPanel
            candidate={viewingCandidate.candidate}
            assessment={viewingAssessment}
            dimensions={dimensions}
            headerActions={<AssessmentExportButton campaignCandidateId={viewingCandidate.id} />}
          />
        ) : null}
      </SlideOverPanel>
      {returnToPoolDialog}
    </div>
  );
}

interface SelectionControlsProps {
  campaignId: string;
  activeIds: string[];
  selectedIds: string[];
  onToggleAll: (selectAll: boolean) => void;
  onClear: () => void;
  onStartOutreach: () => void;
}

function SelectionControls({
  activeIds,
  selectedIds,
  onToggleAll,
  onClear,
  onStartOutreach,
}: SelectionControlsProps) {
  const selectedCount = selectedIds.filter((id) => activeIds.includes(id)).length;
  const allSelected = activeIds.length > 0 && selectedCount === activeIds.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const totalSelected = selectedIds.length;

  const headerCheckboxRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) node.indeterminate = someSelected;
    },
    [someSelected],
  );

  return (
    <div className="mb-2 flex items-center justify-between gap-3 border-b border-border px-3 py-2">
      <label className="flex items-center gap-2 text-xs text-text-muted">
        <input
          ref={headerCheckboxRef}
          type="checkbox"
          aria-label="Select all candidates on this page"
          checked={allSelected}
          onChange={(e) => onToggleAll(e.target.checked)}
          className="h-4 w-4 cursor-pointer rounded border-border accent-accent"
        />
        <span>{totalSelected === 0 ? "Select candidates" : `${totalSelected} selected`}</span>
      </label>
      {totalSelected > 0 && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClear}
            className="rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover hover:text-text-secondary"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onStartOutreach}
            className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90"
          >
            <Send size={12} /> Start outreach
          </button>
        </div>
      )}
    </div>
  );
}
